import React, { useState } from 'react';
import {
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  PrimaryButton,
  TagPicker,
} from 'office-ui-fabric-react';
import { useId } from '@uifabric/react-hooks';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import SubModuleHeader from '../../components/SubModuleHeader';
import DriOperations from '../../actions/driOperations';
import styles from './DriDimensionListApproval.style';

const DriDimensionListApproval = () => {
  const selectedPickerId = useId('generate-selected-dimension-lists');

  const [isGeneratingDLs, setIsGeneratingDLs] = useState(false);
  const formStateMethods = {};

  const [selectedDLIds, setSelectedDLIds] = useState({});
  const [selectedSuccessfulDLIds, setSelectedSuccessfulDLIds] = useState(null);
  const [selectedFailedDLIds, setSelectedFailedDLIds] = useState(null);
  const [selectedDLErrorMessage, setSelectedDLErrorMessage] = useState('');
  formStateMethods[selectedPickerId] = {
    setDLIds: setSelectedDLIds,
    setSuccessfulDLIds: setSelectedSuccessfulDLIds,
    setFailedDLIds: setSelectedFailedDLIds,
    setErrorMessage: setSelectedDLErrorMessage,
  };

  const submitForm = formId => {
    return async params => {
      if (isGeneratingDLs) return;

      setIsGeneratingDLs(true);
      formStateMethods[formId].setSuccessfulDLIds(null);
      formStateMethods[formId].setFailedDLIds(null);
      formStateMethods[formId].setErrorMessage('');

      try {
        const response = await DriOperations.approveMultipleDimensionLists(params);

        let responseJson;
        if (response.ok) {
          responseJson = await response.json();
        }

        if (responseJson) {
          formStateMethods[formId].setSuccessfulDLIds(responseJson.successfulDLs);
          formStateMethods[formId].setFailedDLIds(responseJson.failedDLs);
        } else {
          throw new Error(JSON.stringify(response.error));
        }
      } catch (error) {
        formStateMethods[formId].setErrorMessage(`${error}`);
      }
      setIsGeneratingDLs(false);
    };
  };

  const isDuplicateId = (dLId, dLSet) => {
    return dLSet ? dLSet[dLId] : false;
  };
  const filterSuggestedTags = dLSet => {
    return filterText => {
      if (!filterText || isDuplicateId(filterText, dLSet)) return [];
      return [{ key: filterText, name: filterText }];
    };
  };
  const updateDLIds = formId => {
    return dLIds => {
      const result = {};
      dLIds.forEach(dLId => {
        result[dLId.key] = dLId;
      });
      formStateMethods[formId].setDLIds(result);
    };
  };

  const getTextFromItem = item => item.name;
  const pickerSuggestionsProps = {
    noResultsFoundText: 'Duplicate Dimension List Id',
  };

  return (
    <>
      <Module>
        <ModuleContent>
          <SubModule>
            <SubModuleHeader id="generateSelectDLHeader" data-testid="generateSelectDLHeader">
              Dimension List Approval for a given DL address
            </SubModuleHeader>
            <SubModuleContent>
              <label className={styles.ignoredDLIds} htmlFor={selectedPickerId}>
                Enter any Dimension List addresses to approve
              </label>
              <TagPicker
                removeButtonAriaLabel="Remove"
                onResolveSuggestions={filterText => filterSuggestedTags(selectedDLIds)(filterText)}
                getTextFromItem={getTextFromItem}
                pickerSuggestionsProps={pickerSuggestionsProps}
                removeButtonIconProps={{ iconName: 'Delete' }}
                itemLimit={100}
                pickerCalloutProps={{ doNotLayer: true }}
                inputProps={{
                  id: selectedPickerId,
                }}
                onChange={updateDLIds(selectedPickerId)}
              />
              <PrimaryButton
                disabled={isGeneratingDLs}
                className={styles.searchKeyBtn}
                data-testid="generateSelectDLHeader-submit"
                onClick={() =>
                  submitForm(selectedPickerId)({
                    selectedDLIds: Object.keys(selectedDLIds),
                  })
                }
              >
                {isGeneratingDLs ? (
                  <Spinner size={SpinnerSize.medium} />
                ) : (
                  'Approve Dimension Lists'
                )}
              </PrimaryButton>
              {selectedSuccessfulDLIds && selectedSuccessfulDLIds.length > 0 && (
                <MessageBar
                  className={styles.messageBar}
                  messageBarType={MessageBarType.success}
                  isMultiline={false}
                  onDismiss={() => setSelectedSuccessfulDLIds(null)}
                  dismissButtonAriaLabel="Close"
                >
                  Successfully approved
                  <span> {selectedSuccessfulDLIds.length} </span>
                  Dimension Lists:
                  <span> {JSON.stringify(selectedSuccessfulDLIds)} </span>
                </MessageBar>
              )}
              {selectedFailedDLIds && selectedFailedDLIds.length > 0 && (
                <MessageBar
                  className={styles.messageBar}
                  messageBarType={MessageBarType.severeWarning}
                  isMultiline
                  dismissButtonAriaLabel="Close"
                  onDismiss={() => setSelectedFailedDLIds(null)}
                >
                  Failed to approve
                  <span> {selectedFailedDLIds.length} </span>
                  Dimension Lists:
                  <span> {JSON.stringify(selectedFailedDLIds)} </span>.
                </MessageBar>
              )}
              {selectedDLErrorMessage && (
                <MessageBar
                  className={styles.messageBar}
                  messageBarType={MessageBarType.severeWarning}
                  isMultiline={false}
                  dismissButtonAriaLabel="Close"
                  onDismiss={() => setSelectedDLErrorMessage(null)}
                >
                  {selectedDLErrorMessage}
                </MessageBar>
              )}
            </SubModuleContent>
          </SubModule>
        </ModuleContent>
      </Module>
    </>
  );
};

export default DriDimensionListApproval;
